import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ConfirmationPopup from "../../../../packages/ConfirmationPopup";
import CustomModal from "../../../../packages/CustomModal";
import { useToastNotification } from "../../../../packages/WPToastNotification";

import useTimezone from "../../../../hooks/useTimezone";

import { AssignLicenseFields } from "../../AssignLicenseFields";
import LicensePermissionsAccordion from "../../LicensePermissionsAccordion";

import {
  CustomModalButtons,
  DATE_FORMATS,
} from "../../../../constants/global.constants";
import { HIDDEN_LICENSE_IDS } from "../../../../constants/LicenseManagement.constants";

import { getLicenseStatusByExpiry } from "../../../../utils/LicenseUtils";

import { assignUsersLicense } from "../../../../network/LicenseApis";

const RenewLicense = ({
  userData,
  onCloseModal,
  onUserLicenseUpdate,
  selectedLicenseId,
}) => {
  const { t } = useTranslation();
  const { formatTime } = useTimezone();
  const { showSuccessNotification, showErrorNotification } =
    useToastNotification();

  const licensePermissions =
    useSelector((state) => state?.licensePermissions) || [];

  const { name = "", email = "" } = userData || {};

  const [licenseForm, setLicenseForm] = useState({
    selectedLicenseName: null,
    expiryDate: null,
    keyAccountManager: "",
    isDataChanged: false,
  });
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showRenewConfirm, setShowRenewConfirm] = useState(false);

  const { selectedLicenseName, expiryDate, keyAccountManager } = licenseForm;

  /**
   * Pre-select the correct license in the dropdown when the modal opens for renew.
   * If selectedLicenseId is provided, select that specific license (from view user details).
   * If no selectedLicenseId, select the first valid license (not public default) in the list (from 3-dot menu).
   */
  useEffect(() => {
    if (
      licenseForm.selectedLicenseName ||
      !Array.isArray(userData?.licenses) ||
      userData.licenses.length === 0
    )
      return;

    const validLicenses = userData.licenses.filter(
      (license) => !HIDDEN_LICENSE_IDS.includes(license.id),
    );

    const selectedLicense = selectedLicenseId
      ? validLicenses.find((license) => license.id === selectedLicenseId)
      : validLicenses[0];

    if (!selectedLicense) return;

    setLicenseForm((prev) => ({
      ...prev,
      selectedLicenseName: {
        label: {
          nameType: `${selectedLicense.licenseType || ""} ${selectedLicense.name || ""}`,
          expiry: formatTime(
            selectedLicense.expiryDate,
            true,
            DATE_FORMATS.MMM_DD_YYYY,
          ),
        },
        value: selectedLicense,
      },
    }));
  }, [
    selectedLicenseId,
    userData?.licenses,
    licenseForm.selectedLicenseName,
    formatTime,
  ]);

  const selectedLicenseObj = selectedLicenseName
    ? licensePermissions.find(
        (license) =>
          license.id ===
          (selectedLicenseName.value?.id || selectedLicenseName.value),
      )
    : null;

  const handleLicenseAction = async () => {
    if (!selectedLicenseName) return;
    const { value: licenseObj = {} } = selectedLicenseName;
    const { userId, email } = userData || {};
    const { id, name, assignedDate, licenseType } = licenseObj;
    const expiryDateValue = expiryDate?.format(DATE_FORMATS.YYYY_MM_DD);
    const licenseApproverValue = keyAccountManager;
    const licenseStatuses = getLicenseStatusByExpiry(expiryDateValue);

    const licenseActionPayload = [
      {
        userId,
        email,
        licenses: [
          {
            id,
            name,
            assignedDate,
            expiryDate: expiryDateValue,
            licenseType,
            licenseApprover: licenseApproverValue,
          },
        ],
      },
    ];
    const loadingMsgKey = "licenseManagement.renewingLicense";
    const successMsgKey = "licenseManagement.licenseSuccessfullyRenewed";
    const errorMsgKey = "licenseManagement.licenseRenewalFailed";

    setLoadingMsg(t(loadingMsgKey));
    try {
      await assignUsersLicense(licenseActionPayload);
      showSuccessNotification(t(successMsgKey));

      /** Update the local user license state after successful assign API call */
      const updatedLicense = {
        id,
        name,
        assignedDate,
        expiryDate: expiryDateValue,
        licenseType,
        licenseApprover: licenseApproverValue,
        licenseStatus: licenseStatuses,
      };
      onUserLicenseUpdate?.(userId, updatedLicense);
      onCloseModal();
    } catch {
      showErrorNotification(t(errorMsgKey));
    } finally {
      setLoadingMsg("");
    }
  };

  const handleConfirmBtnClick = ({ target }) => {
    if (target?.id !== CustomModalButtons.CONFIRM) return onCloseModal();
    setShowRenewConfirm(true);
  };

  const handleRenewConfirm = (id) => {
    setShowRenewConfirm(false);
    if (id === CustomModalButtons.CONFIRM) handleLicenseAction();
  };

  return (
    <>
      {!showRenewConfirm && (
        <CustomModal
          customclass="renew-license-modal"
          header={name}
          headerHelper={email}
          confirmBtn={t("licenseManagement.renewLicense")}
          onModalClose={handleConfirmBtnClick}
          width="684px"
          confirmBtnDisabled={!expiryDate}
          profileInitials={name?.charAt(0)?.toUpperCase()}
          isLoading={Boolean(loadingMsg)}
          loadingText={loadingMsg}
          headerDataTestId="renew_license"
          confirmBtnType="primary"
        >
          <AssignLicenseFields
            licenseForm={licenseForm}
            setLicenseForm={setLicenseForm}
            userData={userData}
            dataTestId="renew_license_date"
            isRenewLicense
          />

          <>
            <hr className="modal-section-divider" />
            <LicensePermissionsAccordion licenseObj={selectedLicenseObj} />
          </>
        </CustomModal>
      )}
      {showRenewConfirm && (
        <ConfirmationPopup
          header={t("licenseManagement.renewLicense")}
          description={t("licenseManagement.renewDescription")}
          type="primary"
          confirmBtn={t("licenseManagement.renewButton")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleRenewConfirm}
        />
      )}
    </>
  );
};

export default RenewLicense;
